/**
 * Cmd/Ctrl+S saves the active saved view's unsaved changes.
 *
 * Bound to the document (the user is usually focused in a filter input or on
 * the table, not on the view strip) and registered only while there is
 * something to save, following `SaveBar.tsx`'s Cmd/Ctrl+Enter precedent. Both
 * metaKey and ctrlKey are accepted so the chord works on either platform;
 * Ctrl+S has no native meaning on macOS, so accepting it there is harmless.
 */

import { useEffect } from 'react';

import { isInsideFloatingOverlay } from '@/lib/base/overlays';
import { isMacPlatform } from '@/lib/utils/platform';

interface UseSaveViewShortcutOptions {
  /** A saved view is active and has unsaved changes. */
  enabled: boolean;
  /** A save is already in flight — the chord is ignored until it settles. */
  isSaving: boolean;
  /** Save (owner) or save-as-new (non-owner). Undefined disables the chord. */
  onSave: (() => void) | undefined;
}

/** Hint rendered beside the "Save changes" menu item. */
export function getSaveViewShortcutHint(): string {
  return isMacPlatform() ? '⌘S' : 'Ctrl+S';
}

export function useSaveViewShortcut({
  enabled,
  isSaving,
  onSave,
}: UseSaveViewShortcutOptions): void {
  useEffect(() => {
    if (!enabled || isSaving || !onSave) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.key.toLowerCase() !== 's' ||
        !(e.metaKey || e.ctrlKey) ||
        e.altKey ||
        e.shiftKey
      ) {
        return;
      }
      // A rich-text editor (Plate) owns its own chords — don't steal them.
      const active = document.activeElement as HTMLElement | null;
      if (active?.isContentEditable) {
        return;
      }
      // A modal or slideout on top of the list has its own unsaved state
      // (SaveBar); a view save fired underneath it would be surprising. An
      // open react-aria modal traps focus, so checking where focus currently
      // sits is equivalent to checking whether one is open on top of us.
      if (isInsideFloatingOverlay(active)) {
        return;
      }
      // Always claim the chord: the browser's "Save page" dialog is never
      // what the user wants here.
      e.preventDefault();
      onSave();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, isSaving, onSave]);
}
